import { ReferenceListLabelPipe } from '@/utils/reference-lists/pipes/reference-list-label.pipe';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, type OnInit, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import type { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { primaryColor, sseGreyDark, sseTurquoiseColor } from '../../../constants/color';
import type { Sensor } from '../../../interfaces/Sensor.interface';
import { UnitCode } from '../../../interfaces/Unit.interface';
import { SingleSensorMode, type SingleSensorTranslationKeys } from '../../../interfaces/Widget.interface';
import { LocalNumberPipe } from '../../../pipes/localnumber.pipe';
import { SensorLastPointPipe } from '../../../pipes/sensor-last-point.pipe';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { SensorsProvider } from '../../../providers/sensors/sensors.provider';
import { WeatherProvider } from '../../../providers/weather/weather.provider';
import { WidgetSize } from '../../../providers/widgets.contants';
import { AbstractWidgetClass } from '../AbstractWidget.class';
import type { SingleSensorConfig } from './../../../interfaces/Widget.interface';

@Component({
    styleUrls: ['./widget-single-sensor.scss'],
    selector: 'widget-single-sensor',
    templateUrl: 'widget-single-sensor.html',
    imports: [SvgIconComponent, AsyncPipe, SensorLastPointPipe, LocalNumberPipe, TranslatePipe, ReferenceListLabelPipe]
})
export class WidgetSingleSensorComponent extends AbstractWidgetClass<SingleSensorTranslationKeys, SingleSensorConfig> implements OnInit {
    private _sensorsProvider = inject(SensorsProvider);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _weatherProvider = inject(WeatherProvider);

    size = WidgetSize.HALF;

    sensor$!: Observable<Sensor | undefined>;
    isInsideThreshold$!: Observable<boolean>;
    hasData$!: Observable<boolean>;
    weather$!: Observable<any>;

    outsideTemperatureUnit = UnitCode.CELSIUS;

    positiveColor = '#40776d';
    negativeColor = primaryColor;

    lightPositiveColor = '#daebe8';
    lightNegativeColor = '#fef5f3';

    middlePositiveColor = sseTurquoiseColor;

    lightNoData = 'lightgrey';
    noData = sseGreyDark;

    ngOnInit() {
        this.sensor$ = this._dashboardingProvider.getMeaningfulBuildingId$().pipe(
            switchMap(buildingId => {
                return this._sensorsProvider.getLegacySensorsByBuildingId$(buildingId);
            }),
            map(sensors => {
                if (this.config.mode === SingleSensorMode.FAVORITE) {
                    const universeId = this.config.modeConfig.universeId;
                    return sensors.filter(sensor => sensor.isFavorite && sensor.sensorTypeCode === universeId)[0];
                }

                if (this.config.mode === SingleSensorMode.NAME_PATTERN) {
                    const pattern = this.config.modeConfig.pattern;
                    const universeId = this.config.modeConfig.universeId;

                    return sensors.filter(sensor => {
                        const patternMatched = sensor.name.toLowerCase().match(new RegExp(pattern.toLocaleLowerCase(), 'g'));
                        return isNotNullish(universeId) ? sensor.sensorTypeCode === universeId && patternMatched : patternMatched;
                    })[0];
                }
            }),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        this.hasData$ = this.sensor$.pipe(map(sensor => !!sensor && !!sensor.lastPoint && !!sensor.lastPoint.value));

        this.isInsideThreshold$ = this.sensor$.pipe(
            map(sensor => {
                if (!!sensor && sensor.lastPoint && sensor.lastPoint.value) {
                    return sensor.lastPoint.value <= this.config.higherThreshold && sensor.lastPoint.value >= this.config.lowerThreshold;
                } else {
                    return false;
                }
            })
        );

        this.weather$ = this._dashboardingProvider
            .getMeaningfulBuildingId$()
            .pipe(switchMap(buildingId => this._weatherProvider.getTodayWeather$(buildingId)));
    }
}
